import { Divider, Icon } from "semantic-ui-react";

import React from "react";
import { useTranslation } from "react-i18next";
import { Link, Outlet, useLocation, useNavigate } from "react-router-dom";

import organizationTypes from "astrid-firestore/src/api/organizations/constants/organizationTypes";

import Flex from "../../../../components/Flex/Flex";
import Button from "../../../ui/components/Buttons/Button";
import PrimaryButton from "../../../ui/components/Buttons/PrimaryButton";
import InstantSearch from "../../../ui/components/InstantSearch/InstantSearch";
import InstantSearchBox from "../../../ui/components/InstantSearch/InstantSearchBox";
import InstantSearchClear from "../../../ui/components/InstantSearch/InstantSearchClear";
import InstantSearchHits from "../../../ui/components/InstantSearch/InstantSearchHits";
import InstantSearchHitsPerPage from "../../../ui/components/InstantSearch/InstantSearchHitsPerPage";
import InstantSearchNumberOfHits from "../../../ui/components/InstantSearch/InstantSearchNumberOfHits";
import InstantSearchPagination from "../../../ui/components/InstantSearch/InstantSearchPagination";
import InstantSearchPresets from "../../../ui/components/InstantSearch/InstantSearchPresets";
import InstantSearchRefinements from "../../../ui/components/InstantSearch/InstantSearchRefinements";
import InstantSearchRefresh from "../../../ui/components/InstantSearch/InstantSearchRefresh";
import InstantSearchSorting from "../../../ui/components/InstantSearch/InstantSearchSorting";
import PageView from "../../../ui/views/PageView/PageView";

import { useProfile } from "../../../authentication/state/profile";

import ProductionsDurationCalculation from "./components/ProductionsDurationCalculation";
import ExportCsvDropdown from "./components/ProductionsExportCsv";
import useProductionsRefinements from "./hooks/useProductionsRefinements";
import useProductionsSorting from "./hooks/useProductionsSorting";

const initialIndex = "productions";

export default function ProductionsView({ organization }) {
	const { t } = useTranslation();
	const { pathname } = useLocation();

	const user = useProfile();
	const navigate = useNavigate();

	const sorting = useProductionsSorting();
	const refinements = useProductionsRefinements({ organization });

	const presetsPath = `productions-${organization?.id || "admin"}`;

	const configure = {
		filters: organization && `${organization.type}:${organization.id}`,
	};

	const initialUiState = {
		refinementList: {
			language: user?.[`${organization?.type}s`]?.[organization.id]?.languageOptions,
		},
	};

	return (
		<InstantSearch routing indexName={initialIndex} configure={configure} initialUiState={initialUiState}>
			<InstantSearchRefresh refreshInterval={5000} />

			<PageView
				header={t("productions", "Production")}
				maxWidth={4000}
				extra={
					<>
						{organization && (
							<PrimaryButton as={Link} to={`/${organization.type}/${organization.id}/create-production`}>
								{t("createProduction", "Create production")}
							</PrimaryButton>
						)}
					</>
				}
				sidebar={
					<>
						<InstantSearchBox />

						<InstantSearchRefinements refinements={refinements} />

						<Divider />

						<ProductionsDurationCalculation />
					</>
				}
			>
				<Flex gap={10} justifyContent="flex-start">
					<Flex gap={10} justifyContent="flex-start">
						<InstantSearchPresets path={presetsPath} refinements={refinements} />

						<InstantSearchClear />

						<InstantSearchNumberOfHits />
					</Flex>

					<InstantSearchHitsPerPage />

					<InstantSearchSorting items={sorting} />

					{organization?.type !== organizationTypes.PUBLISHER && (
						<>
							<Button
								icon
								basic
								size="small"
								circular
								active={!pathname.includes("calendar")}
								onClick={() => navigate(`../productions${window.location.search}`)}
							>
								<Icon name="bars" />
							</Button>

							<Button
								icon
								basic
								size="small"
								circular
								active={pathname.includes("calendar")}
								onClick={() => navigate(`calendar${window.location.search}`)}
							>
								<Icon name="calendar alternate " />
							</Button>
						</>
					)}

					<ExportCsvDropdown />
				</Flex>

				<InstantSearchHits>
					{({ data }) => {
						return <Outlet context={{ data, organization }} />;
					}}
				</InstantSearchHits>

				<InstantSearchPagination />
			</PageView>
		</InstantSearch>
	);
}
